<template>
    <div class="manager-container">
        <!-- 标签页切换 -->
        <el-tabs v-model="activeTab" class="content-tabs" type="border-card">
            <!-- 轮播图管理标签页 -->
            <el-tab-pane label="轮播图管理" name="carousel">
                <div class="tab-content">
                    <!-- 上传区域 -->
                    <el-card class="upload-card">
                        <div class="upload-header">
                            <h2>上传新轮播图</h2>
                            <p>支持 JPG、PNG 格式，建议尺寸 1200×600px，文件不超过200k</p>
                        </div>

                        <div class="upload-area">
                            <!-- 排序输入框 - 修复min/max为数字类型 -->
                            <el-form ref="carouselFormRef" :model="carouselForm" :rules="carouselRules"
                                class="carousel-form">
                                <el-form-item label="排序序号" prop="sort">
                                    <el-input-number v-model="carouselForm.sort" :min="1" :max="99"
                                        placeholder="请输入排序序号（数字越小越靠前）"></el-input-number>
                                </el-form-item>
                            </el-form>

                            <!-- 文件上传组件 - 补充action属性（空值占位） -->
                            <el-upload class="upload-demo" drag :auto-upload="false" :on-change="handleFileChange"
                                :file-list="tempFileList" accept="image/jpeg, image/png" :limit="1"
                                :before-upload="beforeUpload" action="#" :on-remove="handleFileRemove">
                                <i class="el-icon-upload"></i>
                                <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                                <!-- 关键优化2：显示文件大小提示，突出200KB限制 -->
                                <div class="el-upload__tip" slot="tip">
                                    只能上传jpg/png文件，<span class="text-danger">大小不超过200KB</span>
                                    <span v-if="tempFileList.length > 0" class="file-size-info">
                                        当前文件：{{ formatFileSize(tempFileList[0].size) }}
                                    </span>
                                </div>
                            </el-upload>

                            <div class="upload-actions">
                                <!-- 预览区域 -->
                                <div v-if="carouselForm.picUrl" class="preview-container">
                                    <h4>预览</h4>
                                    <div class="preview-image">
                                        <img :src="carouselForm.picUrl" alt="轮播图预览">
                                    </div>
                                </div>

                                <div class="button-group">
                                    <el-button type="primary" @click="saveCarousel"
                                        :disabled="tempFileList.length === 0 || !carouselForm.sort || isFileOverSize">保存</el-button>
                                    <el-button @click="resetCarouselForm">取消</el-button>
                                </div>
                            </div>
                        </div>
                    </el-card>

                    <!-- 轮播图列表 -->
                    <el-card class="list-card">
                        <div class="list-header">
                            <h2>轮播图列表</h2>
                            <el-text color="info" size="small">共 {{ carouselList.length }} 张轮播图</el-text>
                        </div>

                        <div class="carousel-list">
                            <div v-if="carouselList.length === 0" class="empty-state">
                                <i class="el-icon-picture-outline"></i>
                                <p>暂无轮播图数据</p>
                            </div>

                            <el-row :gutter="20">
                                <el-col :span="6" v-for="(item, index) in carouselList" :key="item.id"
                                    class="carousel-item">
                                    <div class="carousel-card">
                                        <div class="carousel-image">
                                            <img :src="item.picUrl" :alt="'轮播图 ' + (index + 1)">
                                            <div class="carousel-overlay">
                                                <div class="carousel-actions">
                                                    <el-button icon="el-icon-edit" size="mini" type="primary"
                                                        @click="editCarousel(index)"></el-button>
                                                    <el-button icon="el-icon-delete" size="mini" type="danger"
                                                        @click="deleteCarousel(index)"></el-button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="carousel-meta">
                                            <span class="sort-tag">排序：{{ item.sort }}</span>
                                            <span class="create-time">{{ formatDate(item.createTime) }}</span>
                                        </div>
                                    </div>
                                </el-col>
                            </el-row>
                        </div>
                    </el-card>
                </div>
            </el-tab-pane>

            <!-- 公告管理标签页 -->
            <el-tab-pane label="公告管理" name="announcement">
                <div class="tab-content">
                    <!-- 新增公告区域 -->
                    <el-card class="upload-card">
                        <div class="upload-header">
                            <h2>发布新公告</h2>
                        </div>

                        <el-form ref="announcementForm" :model="announcementForm" :rules="announcementRules"
                            class="announcement-form">
                            <el-form-item label="公告内容" prop="content">
                                <el-input v-model="announcementForm.content" type="textarea" rows="4"
                                    placeholder="请输入公告内容"></el-input>
                            </el-form-item>

                            <el-form-item>
                                <el-button type="primary" @click="saveAnnouncement" :loading="loading">发布</el-button>
                                <el-button @click="resetAnnouncementForm">取消</el-button>
                            </el-form-item>
                        </el-form>
                    </el-card>

                    <!-- 公告列表 -->
                    <el-card class="list-card">
                        <div class="list-header">
                            <h2>公告列表</h2>
                            <el-text color="info" size="small">共 {{ announcementList.length }} 条公告</el-text>
                        </div>

                        <div class="announcement-list">
                            <!-- 加载状态 -->
                            <div v-if="loading" class="loading-state">
                                <el-spinner size="large"></el-spinner>
                                <p>加载中...</p>
                            </div>

                            <!-- 空状态 -->
                            <div v-else-if="announcementList.length === 0" class="empty-state">
                                <i class="el-icon-bell-outline"></i>
                                <p>暂无公告数据</p>
                            </div>

                            <!-- 公告列表 -->
                            <el-table v-else :data="announcementList" border stripe height="30vh">
                                <el-table-column type="index" label="序号" width="60"></el-table-column>
                                <el-table-column prop="content" label="内容">
                                    <template #default="scope">
                                        <div class="content-cell">{{ scope.row.content }}</div>
                                    </template>
                                </el-table-column>

                                <el-table-column prop="createTime" label="发布时间" width="160">
                                    <template #default="scope">
                                        {{ formatDate(scope.row.createTime) }}
                                    </template>
                                </el-table-column>
                                <el-table-column label="操作" width="160">
                                    <template #default="scope">
                                        <el-button icon="el-icon-edit" size="mini" type="primary"
                                            @click="editAnnouncement(scope.$index)"></el-button>
                                        <el-button icon="el-icon-delete" size="mini" type="danger"
                                            @click="deleteAnnouncement(scope.$index)"></el-button>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-card>
                </div>
            </el-tab-pane>
        </el-tabs>
    </div>
</template>

<script>
import { addCarousel, getCarouselList, delCarousel, addNotice, getNoticeList, delNotice } from "@/api/promotion";

export default {
    data() {
        return {
            // 激活的标签页
            activeTab: 'carousel',

            // 轮播图表单数据（适配接口参数）
            carouselForm: {
                picName: '',    // 图片名称
                picType: '小程序轮播图',    // 图片类型
                picUrl: '',     // 图片地址
                sort: 1,        // 排序 - 默认值改为数字1（原0会触发校验失败）
                userId: localStorage.getItem('userId') || '' // 用户ID
            },

            // 轮播图验证规则
            carouselRules: {
                sort: [
                    { required: true, message: '请输入排序序号', trigger: 'blur' },
                    { type: 'number', min: 1, message: '排序序号必须大于0', trigger: 'blur' }
                ]
            },

            // 临时文件列表
            tempFileList: [],

            // 轮播图列表数据
            carouselList: [],

            // 当前编辑的轮播图索引
            editingCarouselIndex: -1,

            // 公告表单数据
            announcementForm: {
                content: '',
                userId: localStorage.getItem('userId') || '' // 添加用户ID
            },

            // 公告表单验证规则
            announcementRules: {
                content: [
                    { required: true, message: '请输入公告内容', trigger: 'blur' },
                    { max: 500, message: '内容不能超过500个字符', trigger: 'blur' }
                ]
            },

            // 公告列表数据
            announcementList: [],

            // 当前编辑的公告索引
            editingAnnouncementIndex: -1,

            // 加载状态
            loading: false,

            // 关键优化3：标记文件是否超过大小限制
            isFileOverSize: false,

            // 最大文件大小限制（200KB）
            maxFileSize: 200 * 1024 // 204800 字节
        };
    },
    async mounted() {
        // 页面加载时获取轮播图列表和公告列表
        await Promise.all([
            this.fetchCarouselList(),
            this.fetchNoticeList()
        ]);
    },
    methods: {
        // 关键优化4：文件大小格式化工具方法（字节转KB/MB）
        formatFileSize(bytes) {
            if (!bytes) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // 获取轮播图列表
        async fetchCarouselList() {
            try {
                const res = await getCarouselList({ userId: localStorage.getItem('userId'), picType: '小程序轮播图' });

                // 适配接口返回数据格式并按排序升序排列
                this.carouselList = res.data.map(item => ({
                    id: item.id,
                    picName: item.picName,
                    picType: item.picType,
                    picUrl: item.picUrl,
                    sort: item.sort,
                    createTime: item.createTime || new Date().toISOString()
                })).sort((a, b) => a.sort - b.sort);

            } catch (error) {
                console.error('获取轮播图列表失败：', error);
                this.$message.error('加载轮播图数据失败');
            }
        },

        // 获取公告列表
        async fetchNoticeList() {
            try {
                this.loading = true;
                // 调用公告列表接口，传入用户ID
                const res = await getNoticeList({ userId: localStorage.getItem('userId') });

                // 适配接口返回数据格式
                this.announcementList = res.data ? res.data.map(item => ({
                    id: item.id,
                    content: item.content,
                    createTime: item.createTime || new Date().toISOString(),
                    userId: item.userId
                })).sort((a, b) => new Date(b.createTime) - new Date(a.createTime)) : [];

            } catch (error) {
                console.error('获取公告列表失败：', error);
                this.$message.error('加载公告数据失败');
                this.announcementList = [];
            } finally {
                this.loading = false;
            }
        },

        // 关键优化5：强化文件上传前校验（精度更高）
        beforeUpload(file) {
            // 1. 校验文件类型（JPG/PNG）
            const isImage = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
            if (!isImage) {
                this.$message.error('只能上传 JPG/PNG 格式图片!');
                return false;
            }

            // 2. 精准校验文件大小（200KB）
            const fileSize = file.size;
            this.isFileOverSize = fileSize > this.maxFileSize;

            if (this.isFileOverSize) {
                this.$message.error(`文件大小超过限制！当前大小：${this.formatFileSize(fileSize)}，最大支持：${this.formatFileSize(this.maxFileSize)}`);
                return false;
            }

            return true;
        },

        // 关键优化6：处理文件选择/变更，实时校验大小
        handleFileChange(file, fileList) {
            this.tempFileList = fileList;

            // 如果有文件，立即校验大小
            if (file.raw) {
                const fileSize = file.size;
                this.isFileOverSize = fileSize > this.maxFileSize;

                // 提取文件信息填充表单
                this.carouselForm.picName = file.name;

                // 生成临时预览URL
                const reader = new FileReader();
                reader.onload = (e) => {
                    // 如果文件大小超限，清空预览
                    if (this.isFileOverSize) {
                        this.carouselForm.picUrl = '';
                        this.$message.warning(`文件 ${file.name} 大小超过200KB限制，无法预览和上传`);
                    } else {
                        this.carouselForm.picUrl = e.target.result;
                    }
                };
                reader.readAsDataURL(file.raw);
            }
        },

        // 关键优化7：文件移除时重置大小标记
        handleFileRemove() {
            this.isFileOverSize = false;
            this.carouselForm.picUrl = '';
        },

        // 保存轮播图（新增/编辑）
        async saveCarousel() {
            // 额外校验：防止绕过前端限制的情况
            if (this.isFileOverSize) {
                this.$message.error('文件大小超过200KB限制，无法上传');
                return;
            }

            // 表单校验
            try {
                await this.$refs.carouselFormRef.validate();
            } catch (error) {
                this.$message.error('请完善表单信息');
                return;
            }

            if (this.tempFileList.length === 0) {
                this.$message.error('请上传轮播图片');
                return;
            }

            const file = this.tempFileList[0].raw;
            if (!file) {
                this.$message.error('文件格式异常');
                return;
            }

            try {
                // 组装表单数据（包含文件）
                const formData = new FormData();
                formData.append('file', file);
                formData.append('picName', this.carouselForm.picName);
                formData.append('picType', this.carouselForm.picType); // 固定值
                formData.append('sort', this.carouselForm.sort);
                formData.append('userId', this.carouselForm.userId);

                // 调用新增接口（编辑逻辑：删除原数据 + 新增新数据）
                if (this.editingCarouselIndex !== -1) {
                    // 编辑模式：先删除原轮播图
                    await delCarousel({
                        id: this.carouselList[this.editingCarouselIndex].id,
                        userId: this.carouselForm.userId
                    });
                }

                // 调用上传接口
                const res = await addCarousel(formData);
                if (res.code) {
                    this.$message.success(this.editingCarouselIndex === -1 ? '轮播图添加成功' : '轮播图编辑成功');
                    await this.fetchCarouselList(); // 重新获取列表
                    this.resetCarouselForm(); // 重置表单
                } else {
                    this.$message.error(this.editingCarouselIndex === -1 ? '添加失败：' + res.message : '编辑失败：' + res.message);
                }
            } catch (error) {
                console.error('保存轮播图失败：', error);
                this.$message.error('操作失败，请重试');
            }
        },

        // 重置轮播图表单
        resetCarouselForm() {
            this.tempFileList = [];
            this.isFileOverSize = false; // 重置大小标记
            this.carouselForm = {
                picName: '',
                picType: '小程序轮播图', // 保留固定类型
                picUrl: '',
                sort: 1, // 默认值改为1
                userId: localStorage.getItem('userId') || ''
            };
            this.$refs.carouselFormRef?.resetFields();
            this.editingCarouselIndex = -1;
        },

        // 编辑轮播图
        editCarousel(index) {
            const item = this.carouselList[index];
            // 填充表单数据
            this.carouselForm.picName = item.picName;
            this.carouselForm.picType = item.picType;
            this.carouselForm.picUrl = item.picUrl;
            this.carouselForm.sort = item.sort;
            this.editingCarouselIndex = index;

            // 滚动到顶部，方便操作
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },

        // 删除轮播图
        async deleteCarousel(index) {
            try {
                await this.$confirm('确定要删除这张轮播图吗？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                const item = this.carouselList[index];
                const res = await delCarousel({ id: item.id, userId: this.carouselForm.userId });

                this.$message.success(res.message);
                await this.fetchCarouselList(); // 重新获取列表

            } catch (error) {
                // 取消删除或接口失败的处理
                if (error !== 'cancel') {
                    console.error('删除轮播图失败：', error);
                    this.$message.error('删除操作异常');
                }
            }
        },

        // 保存公告（新增/编辑）
        async saveAnnouncement() {
            this.$refs.announcementForm.validate(async (valid) => {
                if (valid) {
                    try {
                        this.loading = true;
                        // 组装公告参数
                        const noticeParams = {
                            content: this.announcementForm.content,
                            userId: this.announcementForm.userId
                        };

                        if (this.editingAnnouncementIndex === -1) {
                            // 新增公告
                            const res = await addNotice(noticeParams);
                            if (res.code) {
                                this.$message.success('公告发布成功');
                            } else {
                                this.$message.error('发布失败：' + res.message);
                                return;
                            }
                        } else {
                            // 编辑公告 - 先删除原公告再新增（如果接口不支持直接编辑）
                            const currentNotice = this.announcementList[this.editingAnnouncementIndex];
                            await delNotice({ id: currentNotice.id, userId: this.announcementForm.userId });

                            const res = await addNotice(noticeParams);
                            if (res.code) {
                                this.$message.success('公告更新成功');
                            } else {
                                this.$message.error('更新失败：' + res.message);
                                return;
                            }
                            this.editingAnnouncementIndex = -1;
                        }

                        // 重新获取公告列表并重置表单
                        await this.fetchNoticeList();
                        this.resetAnnouncementForm();

                    } catch (error) {
                        console.error('保存公告失败：', error);
                        this.$message.error('操作失败，请重试');
                    } finally {
                        this.loading = false;
                    }
                }
            });
        },

        // 重置公告表单
        resetAnnouncementForm() {
            this.$refs.announcementForm.resetFields();
            this.announcementForm = {
                content: '',
                userId: localStorage.getItem('userId') || ''
            };
            this.editingAnnouncementIndex = -1;
        },

        // 编辑公告
        editAnnouncement(index) {
            const item = this.announcementList[index];
            this.announcementForm.content = item.content;
            this.editingAnnouncementIndex = index;

            // 切换到公告标签页并滚动到顶部
            this.activeTab = 'announcement';
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },

        // 删除公告
        async deleteAnnouncement(index) {
            try {
                await this.$confirm('确定要删除这条公告吗？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                this.loading = true;
                const item = this.announcementList[index];
                const res = await delNotice({ id: item.id, userId: localStorage.getItem('userId') });

                if (res.code) {
                    this.$message.success('公告已删除');
                    await this.fetchNoticeList(); // 重新获取列表
                } else {
                    this.$message.error('删除失败：' + res.message);
                }

            } catch (error) {
                // 取消删除或接口失败的处理
                if (error !== 'cancel') {
                    console.error('删除公告失败：', error);
                    this.$message.error('删除操作异常');
                }
            } finally {
                this.loading = false;
            }
        },

        // 格式化日期
        formatDate(date) {
            if (!date) return '';

            if (!(date instanceof Date)) {
                date = new Date(date);
            }

            // 处理无效日期
            if (isNaN(date.getTime())) {
                return '';
            }

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}`;
        }
    }
};
</script>

<style lang="scss" scoped>
.manager-container {
    padding: 20px;
    background-color: #f5f7fa;
    min-height: 100vh;
}

.content-tabs {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.tab-content {
    padding: 20px;
}

.upload-card {
    margin-bottom: 20px;

    .upload-header {
        margin-bottom: 15px;

        h2 {
            font-size: 18px;
            color: #333;
            margin-bottom: 5px;
            font-weight: 500;
        }

        p {
            color: #666;
            font-size: 13px;
        }
    }

    .carousel-form {
        margin-bottom: 15px;

        .el-form-item {
            margin-bottom: 10px;
            width: 300px;
        }
    }
}

.upload-area {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    align-items: center;

    .upload-demo {
        flex: 1;
        min-width: 300px;
    }

    .upload-actions {
        flex: 1;
        min-width: 300px;
        display: flex;
        flex-direction: column;
        gap: 20px;

        .preview-container {
            margin-top: 15px;

            h4 {
                font-size: 14px;
                color: #333;
                margin-bottom: 10px;
                font-weight: 500;
            }

            .preview-image {
                border: 1px dashed #e8e8e8;
                border-radius: 4px;
                padding: 10px;
                text-align: center;

                img {
                    max-width: 100%;
                    max-height: 200px;
                    border-radius: 4px;
                }
            }
        }

        .button-group {
            margin-top: 15px;
        }
    }
}

// 关键优化8：文件大小提示样式
.file-size-info {
    margin-left: 10px;
    color: #666;

    &.text-danger {
        color: #f56c6c;
    }
}

.announcement-form {
    .el-form-item {
        margin-bottom: 15px;
    }
}

.list-card {
    height: 50vh;
    overflow: auto;
    position: relative;

    .list-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;

        h2 {
            font-size: 18px;
            color: #333;
            font-weight: 500;
        }
    }
}

// 加载状态样式
.loading-state {
    text-align: center;
    padding: 40px 0;
    color: #999;

    .el-spinner {
        margin-bottom: 10px;
    }

    p {
        font-size: 14px;
        margin-top: 10px;
    }
}

.carousel-list {
    .empty-state {
        text-align: center;
        padding: 40px 0;
        color: #999;

        i {
            font-size: 48px;
            margin-bottom: 10px;
            color: #ddd;
        }
    }

    .carousel-item {
        margin-bottom: 20px;
    }

    .carousel-card {
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        overflow: hidden;
        transition: all 0.3s;

        &:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            border-color: transparent;
        }

        .carousel-image {
            height: 160px;
            overflow: hidden;
            position: relative;

            img {
                width: 100%;
                height: 100%;
                transition: transform 0.5s;

                &:hover {
                    transform: scale(1.05);
                }
            }

            .carousel-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                align-items: center;
                justify-content: center;
                opacity: 0;
                transition: opacity 0.3s;

                &:hover {
                    opacity: 1;
                }

                .carousel-actions {
                    display: flex;
                    gap: 10px;
                }
            }
        }

        .carousel-meta {
            padding: 10px 15px;
            font-size: 12px;
            color: #999;
            border-top: 1px solid #f5f5f5;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .sort-tag {
                background-color: #f0f9ff;
                color: #409eff;
                padding: 2px 6px;
                border-radius: 3px;
            }
        }
    }
}

.announcement-list {
    .content-cell {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .empty-state {
        text-align: center;
        padding: 40px 0;
        color: #999;

        i {
            font-size: 48px;
            margin-bottom: 10px;
            color: #ddd;
        }
    }
}

@media (max-width: 768px) {
    .upload-area {
        flex-direction: column;
    }

    .carousel-item {
        width: 100% !important;
    }

    .carousel-form {
        width: 100% !important;
    }
}
</style>